/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as QueryString from 'querystring';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { getSecretValue } from '../shared/functions/get-secret';
/**
 * UpdateB2cIdentity Input Action
 */
export class UpdateB2cIdentityInput implements Msdyn365.IActionInput {
    private _random: number;
    public token: string;
    public newEmailAddress: string;

    constructor(newEmailAddress: string, token: string) {
        this._random = Math.random();
        this.newEmailAddress = newEmailAddress;
        this.token = token;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `UpdateB2cIdentity${this._random}`;
    public getCacheObjectType = () => 'UpdateB2cIdentity';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IUpdateB2cIdentityData {
    success: boolean;
    error?: {
        reason: 'duplicate' | 'invalid' | 'other';
        message: string;
    };
    data: any;
}

/**
 * Creates input from the postdata
 */
const createInput = (ctx: Msdyn365.ICreateActionContext): UpdateB2cIdentityInput => {
    let {
        requestContext: {
            user: { token },
            // eslint-disable-next-line prefer-const
            postBody
        }
    } = ctx;
    let email = '';
    // postBody of the context should contain token and email needed to communicate with Graph API
    if (postBody?.content) {
        const content = JSON.parse(postBody.content.toString()) as UpdateB2cIdentityInput;
        // prefer token explicitly passed in
        token = content.token;
        email = content.newEmailAddress;
    }
    return new UpdateB2cIdentityInput(email, token);
};

interface TokenResponse {
    token_type: string;
    access_token: string;
}
interface GraphResponse {
    error?: {
        code: string;
        /**
         * duplicate email = "A conflicting object with one or more of the specified property values is present in the directory."
         * invalid email = "One or more properties contains invalid values."
         */
        message: string;
        details?: {
            code: string; //ConflictingObjects === duplicate email
            message: string;
        }[];
    };
}

async function action(input: UpdateB2cIdentityInput, ctx: Msdyn365.IActionContext): Promise<IUpdateB2cIdentityData> {
    const {
        requestContext: {
            apiSettings: { baseUrl }
        }
    } = ctx;
    // decode token to extract guid that points to user in AAD B2C
    const decoded: JwtPayload = jwtDecode(input.token);

    // secrets stored in key vault
    const secretPromises = [
        getSecretValue('ecom-36954-my-account-tenantId', baseUrl),
        getSecretValue('ecom-36954-my-account-applicationId', baseUrl),
        getSecretValue('ecom-36954-my-account-secret', baseUrl)
    ];

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    return Promise.all<string | undefined>(secretPromises)
        .then(secrets => {
            return Msdyn365.sendRequest<TokenResponse>(
                `https://login.microsoft.com/${secrets[0]}/oauth2/token`,
                'post',
                QueryString.stringify({
                    grant_type: 'client_credentials',
                    client_id: secrets[1],
                    client_secret: secrets[2],
                    resource: 'https://graph.microsoft.com'
                })
            );
        })
        .then(tokenResponse => {
            const url = new URL(decoded.iss!);
            const issuerDomain = url.hostname.substring(0, url.hostname.indexOf('.'));
            return Msdyn365.sendRequest<GraphResponse>(
                `https://graph.microsoft.com/v1.0/users/${decoded.sub}`,
                'patch',
                JSON.stringify({
                    identities: [
                        {
                            signInType: 'emailAddress',
                            issuer: `${issuerDomain}.onmicrosoft.com`,
                            issuerAssignedId: input.newEmailAddress
                        }
                    ]
                }),
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${tokenResponse.data.access_token}`
                    }
                }
            );
        })
        .then(graphResponse => {
            const result: IUpdateB2cIdentityData = {
                success: graphResponse.status >= 200 && graphResponse.status < 300,
                data: graphResponse.data
            };
            if (!result.success && graphResponse.data.error) {
                result.error = {
                    message: graphResponse.data.error.message,
                    reason:
                        graphResponse.data.error.message.indexOf('conflicting') >= 0
                            ? 'duplicate'
                            : graphResponse.data.error.message.indexOf('invalid values') >= 0
                            ? 'invalid'
                            : 'other'
                };
            }
            return result;
        });
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IUpdateB2cIdentityData>>action,
    id: 'UpdateB2cIdentity',
    input: createInput
});
